import { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { type IntlShape } from "react-intl";
import {
  formatRevenueAmount,
  getConnectorNameForFE,
  getEndDateForPeriodText,
  getMissingPeriodString,
  getPlatformStringFromConnectionCategory,
  isInsufficientL60dRevenueAIR,
} from "shared-ui";

import { MESSAGES } from "./message-definitions";

dayjs.extend(LocalizedFormat);

const getBankStatementsSubtext = (
  intl: IntlShape,
  airData: FundingApplicationSchemas.BankStatementsForAccountAIRSchema
): string => {
  const bankName = airData.bank_name || intl.formatMessage(MESSAGES.bankStatementsDefaultBankName);
  const periods = airData.periods
    .map((period) =>
      intl.formatMessage(MESSAGES.bankStatementsPeriodText, {
        dateFrom: dayjs(period.start).startOf("month").format("LL"),
        dateTo: getEndDateForPeriodText(period.end).format("LL"),
      })
    )
    .join("; ");
  return airData.bank_account_mask
    ? intl.formatMessage(MESSAGES.bankStatementsSubtextWithMask, {
        bankName,
        accountMask: airData.bank_account_mask,
        periods,
      })
    : intl.formatMessage(MESSAGES.bankStatementsSubtext, { bankName, periods });
};

const getRevenueSourceSubtext = (
  intl: IntlShape,
  airData: FundingApplicationSchemas.AdditionalRevenueSourceAIRSchema
): string => {
  const hasInsufficientData = airData.reasons.includes("insufficient_revenue_data");
  const hasInsufficientRevenue = airData.reasons.includes("customer_revenue_not_sufficient_for_funding");

  if (hasInsufficientData && hasInsufficientRevenue) {
    return intl.formatMessage(MESSAGES.revenueSourceMixedSubtext, {
      startPeriod: dayjs(airData.missing_period?.start).format("LL"),
      endPeriod: getEndDateForPeriodText(airData.missing_period?.end).format("LL"),
      period: getMissingPeriodString(airData.missing_period, intl),
      thresholdAmount: formatRevenueAmount(airData.mean_30d_revenue_threshold, intl),
    });
  }
  if (hasInsufficientRevenue) {
    return intl.formatMessage(MESSAGES.revenueSourceInsufficientRevenueSubtext, {
      thresholdAmount: formatRevenueAmount(airData.mean_30d_revenue_threshold, intl),
      insufficientPeriod: isInsufficientL60dRevenueAIR(airData) ? 2 : 6,
    });
  }
  if (hasInsufficientData) {
    return intl.formatMessage(MESSAGES.revenueSourceMissingPeriodSubtext, {
      startPeriod: dayjs(airData.missing_period?.start).format("LL"),
      endPeriod: getEndDateForPeriodText(airData.missing_period?.end).format("LL"),
    });
  }
  return intl.formatMessage(MESSAGES.revenueSourceDefaultSubtext);
};

const getReconnectConnectionSubtext = (
  intl: IntlShape,
  airData: FundingApplicationSchemas.ReconnectConnectionAIRSchema
): string => {
  if (airData.connection_meta_data?.type === "BANKING") {
    const metaData = airData.connection_meta_data as FundingApplicationSchemas.ReconnectBankConnectionMetaData;
    const bankName = airData.connection_identifier ?? "";
    if (metaData.account_masks.length === 0) {
      return intl.formatMessage(MESSAGES.reconnectBankNoMaskSubtext, { bankName });
    }
    if (metaData.account_masks.length === 1) {
      return intl.formatMessage(MESSAGES.reconnectBankSingleMaskSubtext, {
        bankName,
        singularMask: metaData.account_masks[0] ?? "",
      });
    }
    return intl.formatMessage(MESSAGES.reconnectBankMultiMaskSubtext, {
      bankName,
      maskList: metaData.account_masks.slice(0, -1).join(", "),
      maskListFinal: metaData.account_masks[metaData.account_masks.length - 1] ?? "",
    });
  }
  const platform = getConnectorNameForFE(airData.connection_type);
  return airData.connection_identifier
    ? intl.formatMessage(MESSAGES.reconnectPlatformWithIdentifierSubtext, {
        platform,
        identifier: airData.connection_identifier,
      })
    : intl.formatMessage(MESSAGES.reconnectPlatformSubtext, { platform });
};

export const getAirTitle = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): string => {
  switch (air.type) {
    case "bank_statements_for_account":
      return intl.formatMessage(MESSAGES.bankStatementsTitle);
    case "additional_revenue_source":
      return intl.formatMessage(MESSAGES.revenueSourceTitle);
    case "reconnect_connection": {
      const data = air.data as FundingApplicationSchemas.ReconnectConnectionAIRSchema;
      return intl.formatMessage(MESSAGES.reconnectConnectionTitle, {
        platform: getPlatformStringFromConnectionCategory(intl, data.connection_meta_data?.type, data.connection_type),
      });
    }
    default:
      return intl.formatMessage(MESSAGES.defaultTitle);
  }
};

// Translation key backing the title, used as a stable `taskUniqueId` for the TaskCard.
export const getAirTitleKey = (air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema): string => {
  switch (air.type) {
    case "bank_statements_for_account":
      return MESSAGES.bankStatementsTitle.id;
    case "additional_revenue_source":
      return MESSAGES.revenueSourceTitle.id;
    case "reconnect_connection":
      return MESSAGES.reconnectConnectionTitle.id;
    default:
      return MESSAGES.defaultTitle.id;
  }
};

export const getAirDescription = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): string => {
  switch (air.type) {
    case "bank_statements_for_account":
      return getBankStatementsSubtext(intl, air.data as FundingApplicationSchemas.BankStatementsForAccountAIRSchema);
    case "additional_revenue_source":
      return getRevenueSourceSubtext(intl, air.data as FundingApplicationSchemas.AdditionalRevenueSourceAIRSchema);
    case "reconnect_connection":
      return getReconnectConnectionSubtext(intl, air.data as FundingApplicationSchemas.ReconnectConnectionAIRSchema);
    default:
      return intl.formatMessage(MESSAGES.defaultSubtext);
  }
};

export const getAirTimeEstimate = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): string | undefined => {
  switch (air.type) {
    case "bank_statements_for_account":
      return intl.formatMessage(MESSAGES.bankStatementsTimeEstimate);
    case "additional_revenue_source":
      return intl.formatMessage(MESSAGES.revenueSourceTimeEstimate);
    case "reconnect_connection":
      return intl.formatMessage(MESSAGES.reconnectConnectionTimeEstimate);
    default:
      return undefined;
  }
};

export const getAirCta = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): string => {
  switch (air.type) {
    case "bank_statements_for_account":
      return intl.formatMessage(MESSAGES.bankStatementsCta);
    case "additional_revenue_source":
      return intl.formatMessage(MESSAGES.revenueSourceCta);
    case "reconnect_connection":
      return intl.formatMessage(MESSAGES.reconnectConnectionCta);
    default:
      return intl.formatMessage(MESSAGES.defaultCta);
  }
};
